import { useEffect, useState } from "react";
import { adminLogin, fetchLeads, formatApiErrorDetail, Lead, PROFILE_PDF_URL, updateLeadStatus } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { usePageMeta } from "@/lib/meta";
import { waLink } from "@/lib/site";
import { WhatsAppIcon } from "@/components/Footer";
import { LogoMark } from "@/components/Logo";
import GoogleSignInButton from "@/components/GoogleSignInButton";

const STATUSES = ["New", "Contacted", "In Consultation", "Closed"];

export default function Admin() {
  usePageMeta("Studio Admin — Leads | Shriya Redkar Interiors");
  const { user, loading: authLoading, logout: googleLogout } = useAuth();
  const [token, setToken] = useState<string | null>(() => sessionStorage.getItem("sri_admin_token"));
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [stats, setStats] = useState<Record<string, any> | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const googleAdmin = user?.role === "admin";
  const authed = !!token || googleAdmin;

  const load = async (t: string | null) => {
    setRefreshing(true);
    try {
      const data = await fetchLeads(t);
      setLeads(data.leads);
      setStats(data.stats);
      setError("");
    } catch (e: any) {
      if (e.response?.status === 401 && t) {
        sessionStorage.removeItem("sri_admin_token");
        setToken(null);
      }
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (authed) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, token]);

  const onLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { token: t } = await adminLogin(password);
      sessionStorage.setItem("sri_admin_token", t);
      setToken(t);
      setPassword("");
    } catch (e: any) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    sessionStorage.removeItem("sri_admin_token");
    setToken(null);
    setLeads([]);
    setStats(null);
    if (googleAdmin) await googleLogout();
  };

  const onStatus = async (lead: Lead, status: string) => {
    if (!authed || status === lead.status) return;
    try {
      await updateLeadStatus(token, lead._id, status);
      setLeads((ls) => ls.map((l) => (l._id === lead._id ? { ...l, status } : l)));
    } catch (e: any) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  if (authLoading && !token) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-obsidian">
        <p className="font-mono text-sm text-zinc-500">Checking your session…</p>
      </main>
    );
  }

  if (!authed) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-obsidian px-6">
        <form
          onSubmit={onLogin}
          data-testid="admin-login-form"
          className="w-full max-w-sm border border-white/10 bg-obsidian-card p-10"
        >
          <LogoMark className="mx-auto h-14 w-14" />
          <h1 className="mt-6 text-center font-serif text-3xl text-zinc-50">Studio Admin</h1>
          <p className="mt-2 text-center text-xs text-zinc-500">Leads dashboard — authorised studio accounts only.</p>

          <GoogleSignInButton returnTo="/admin" label="Sign in with Google" className="mt-8 w-full" testId="admin-google-signin" />
          {user && !googleAdmin && (
            <p className="mt-4 border-l-2 border-amber-400 bg-amber-400/10 px-4 py-3 text-xs text-amber-200" data-testid="admin-not-authorised">
              {user.email} isn't authorised for the dashboard. Use the studio password below or sign in with an approved Google account.
            </p>
          )}

          <div className="my-7 flex items-center gap-4 text-[10px] uppercase tracking-[0.2em] text-zinc-600">
            <span className="h-px flex-1 bg-white/10" /> or password <span className="h-px flex-1 bg-white/10" />
          </div>

          <input
            type="password"
            data-testid="admin-login-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Admin password"
            className="w-full border border-white/15 bg-obsidian-soft px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-gold focus:outline-none"
          />
          {error && (
            <p className="mt-4 border-l-2 border-red-500 bg-red-500/10 px-4 py-3 text-sm text-red-300" data-testid="admin-login-error">
              {error}
            </p>
          )}
          <button type="submit" disabled={loading} data-testid="admin-login-button" className="btn-gold mt-6 w-full disabled:opacity-60">
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-obsidian px-5 py-10 sm:px-8 lg:px-12">
      <header className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <LogoMark className="h-10 w-10" />
          <div>
            <h1 className="font-serif text-2xl text-zinc-50">Leads dashboard</h1>
            <p className="text-xs text-zinc-500">
              Every enquiry from the website, newest first.{googleAdmin && user ? ` Signed in as ${user.email}.` : ""}
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <a href={PROFILE_PDF_URL} target="_blank" rel="noopener noreferrer" data-testid="admin-profile-pdf-link" className="btn-ghost !px-5 !py-2.5">
            Studio profile PDF
          </a>
          <button onClick={() => load(token)} data-testid="admin-refresh-button" className="btn-ghost !px-5 !py-2.5">
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
          <button onClick={logout} data-testid="admin-logout-button" className="btn-ghost !px-5 !py-2.5 hover:!border-red-400 hover:!text-red-400">
            Sign out
          </button>
        </div>
      </header>

      {stats && (
        <section className="mx-auto mt-8 grid max-w-7xl grid-cols-2 gap-4 lg:grid-cols-4" data-testid="admin-stats">
          {[
            { k: "Total leads", v: stats.total },
            { k: "New", v: stats.new },
            { k: "This week", v: stats.this_week },
            { k: "₹50L+ enquiries", v: stats.premium },
          ].map((s) => (
            <div key={s.k} className="border border-white/10 bg-obsidian-card p-5">
              <p className="font-serif text-3xl text-gold">{s.v}</p>
              <p className="mt-1 text-[11px] uppercase tracking-[0.16em] text-zinc-500">{s.k}</p>
            </div>
          ))}
        </section>
      )}

      {error && (
        <p className="mx-auto mt-6 max-w-7xl border-l-2 border-red-500 bg-red-500/10 px-4 py-3 text-sm text-red-300" data-testid="admin-error">
          {error}
        </p>
      )}

      <section className="mx-auto mt-8 max-w-7xl overflow-x-auto border border-white/10" data-testid="admin-leads-table">
        <table className="w-full min-w-[860px] text-left text-sm">
          <thead>
            <tr className="border-b border-white/10 bg-obsidian-soft font-display text-[10px] uppercase tracking-[0.18em] text-zinc-500">
              <th className="px-4 py-3">Received</th>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Phone</th>
              <th className="px-4 py-3">City</th>
              <th className="px-4 py-3">Property</th>
              <th className="px-4 py-3">Budget</th>
              <th className="px-4 py-3">Timeline</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Chat</th>
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => (
              <tr key={l._id} data-testid={`lead-row-${l._id}`} className="border-b border-white/5 text-zinc-300 transition-colors hover:bg-white/[0.03]">
                <td className="whitespace-nowrap px-4 py-3 text-xs text-zinc-500">
                  {new Date(l.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}{" "}
                  {new Date(l.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                </td>
                <td className="px-4 py-3 font-medium text-zinc-100">{l.name}</td>
                <td className="px-4 py-3 font-mono text-xs">{l.phone}</td>
                <td className="px-4 py-3">{l.city}</td>
                <td className="px-4 py-3 text-xs">{l.property_type}</td>
                <td className="px-4 py-3 text-gold">{l.budget_band}</td>
                <td className="px-4 py-3 text-xs">{l.timeline}</td>
                <td className="px-4 py-3">
                  <select
                    value={l.status}
                    onChange={(e) => onStatus(l, e.target.value)}
                    data-testid={`lead-status-select-${l._id}`}
                    className="border border-white/15 bg-obsidian-soft px-2 py-1.5 text-xs text-zinc-200 focus:border-gold focus:outline-none"
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <a
                    href={waLink(`Hi ${l.name}, this is the ${l.city} team from Shriya Redkar Interiors following up on your enquiry.`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`lead-whatsapp-${l._id}`}
                    className="inline-flex items-center gap-1.5 text-xs text-wa hover:underline"
                  >
                    <WhatsAppIcon className="h-3.5 w-3.5" /> Open
                  </a>
                </td>
              </tr>
            ))}
            {leads.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-12 text-center text-zinc-500">
                  No enquiries yet — they'll appear here the moment someone submits the form.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </main>
  );
}
